// User tracking utility for ADA service usage
export class UserTracker {
  constructor() {
    this.storageKey = 'ada_usage_tracking';
    this.initStorage();
  }

  initStorage() {
    if (!localStorage.getItem(this.storageKey)) {
      localStorage.setItem(this.storageKey, JSON.stringify([]));
    }
  }

  // Get user's IP address and location
  async getUserLocation() {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return {
        ip: data.ip || 'Unknown',
        country: data.country_name || 'Unknown',
        region: data.region || 'Unknown',
        city: data.city || 'Unknown',
        timezone: data.timezone || 'Unknown'
      };
    } catch (error) {
      console.warn('Could not fetch location data:', error);
      return {
        ip: 'Unknown',
        country: 'Unknown',
        region: 'Unknown',
        city: 'Unknown',
        timezone: 'Unknown'
      };
    }
  }

  // Get browser and device information
  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';

    // Detect browser
    if (userAgent.includes('Chrome')) browser = 'Chrome';
    else if (userAgent.includes('Firefox')) browser = 'Firefox';
    else if (userAgent.includes('Safari')) browser = 'Safari';
    else if (userAgent.includes('Edge')) browser = 'Edge';
    else if (userAgent.includes('Opera')) browser = 'Opera';

    // Detect OS
    if (userAgent.includes('Windows')) os = 'Windows';
    else if (userAgent.includes('Mac')) os = 'macOS';
    else if (userAgent.includes('Linux')) os = 'Linux';
    else if (userAgent.includes('Android')) os = 'Android';
    else if (userAgent.includes('iOS')) os = 'iOS';

    return {
      browser,
      os,
      userAgent,
      screenResolution: `${screen.width}x${screen.height}`,
      language: navigator.language || 'Unknown'
    };
  }

  // Track ADA service usage
  async trackUsage(data) {
    try {
      const locationData = await this.getUserLocation();
      const browserInfo = this.getBrowserInfo();
      
      const trackingEntry = {
        id: this.generateId(),
        timestamp: new Date().toISOString(),
        url: data.url || 'Unknown',
        testType: data.testType || 'ADA Compliance Check',
        duration: data.duration || null,
        issues_found: data.issuesFound || 0,
        success: data.success || false,
        location: locationData,
        browser: browserInfo,
        session_id: this.getSessionId(),
        user_id: this.getUserId()
      };

      const existingData = this.getStoredData();
      existingData.push(trackingEntry);
      localStorage.setItem(this.storageKey, JSON.stringify(existingData));

      return trackingEntry;
    } catch (error) {
      console.error('Error tracking usage:', error);
      return null;
    }
  }

  // Generate unique ID
  generateId() {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Get or create session ID
  getSessionId() {
    let sessionId = sessionStorage.getItem('ada_session_id');
    if (!sessionId) {
      sessionId = this.generateId();
      sessionStorage.setItem('ada_session_id', sessionId);
    }
    return sessionId;
  }

  // Get or create user ID (persistent across sessions)
  getUserId() {
    let userId = localStorage.getItem('ada_user_id');
    if (!userId) {
      userId = this.generateId();
      localStorage.setItem('ada_user_id', userId);
    }
    return userId;
  }

  // Get stored tracking data
  getStoredData() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return JSON.parse(data) || [];
    } catch (error) {
      console.error('Error reading stored data:', error);
      return [];
    }
  }

  // Get usage statistics
  getUsageStats() {
    const data = this.getStoredData();
    const totalTests = data.length;
    const successfulTests = data.filter(entry => entry.success).length;
    const uniqueUrls = new Set(data.map(entry => entry.url)).size;
    const uniqueUsers = new Set(data.map(entry => entry.user_id)).size;
    
    // Country statistics
    const countryStats = {};
    data.forEach(entry => {
      const country = entry.location?.country || 'Unknown';
      countryStats[country] = (countryStats[country] || 0) + 1;
    });

    // Recent activity (last 24 hours)
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recentActivity = data.filter(entry => 
      new Date(entry.timestamp) > oneDayAgo
    ).length;

    return {
      totalTests,
      successfulTests,
      uniqueUrls,
      uniqueUsers,
      countryStats,
      recentActivity,
      successRate: totalTests > 0 ? (successfulTests / totalTests * 100).toFixed(1) : 0
    };
  }

  // Export data for admin panel
  exportData(filters = {}) {
    let data = this.getStoredData();

    // Apply filters
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      data = data.filter(entry => new Date(entry.timestamp) >= startDate);
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      data = data.filter(entry => new Date(entry.timestamp) <= endDate);
    }

    if (filters.country) {
      data = data.filter(entry => 
        entry.location?.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }

    if (filters.url) {
      data = data.filter(entry => 
        entry.url?.toLowerCase().includes(filters.url.toLowerCase())
      );
    }

    return data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  // Clear old data (keep only last 1000 entries)
  cleanupOldData() {
    const data = this.getStoredData();
    if (data.length > 1000) {
      const recentData = data.slice(-1000);
      localStorage.setItem(this.storageKey, JSON.stringify(recentData));
    }
  }
}

// Create singleton instance
export const userTracker = new UserTracker();

// Export convenience functions
export const trackADAUsage = (data) => userTracker.trackUsage(data);
export const getUsageStats = () => userTracker.getUsageStats();
export const exportUsageData = (filters) => userTracker.exportData(filters);